import { Collapse, Grid, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Answers,
	Feedback,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { ReviewerResultTag } from '../ReviewerResultTag';
import Icon from '../../../components/ui-components/Icon';
import GrayChevronDown from '../../../assets/images/gray-chevron-down.svg';
import GreenTick from '../../../assets/images/green-tick.svg';
import RedTick from '../../../assets/images/red-tick.svg';
import { CategoryStepUIConfig } from '../../../shared/CategoryStepUIConfig';
import { ReviewAction } from '../../../shared/Constants';

const { Panel } = Collapse;
const { Text } = Typography;
const { useBreakpoint } = Grid;

export const Header = ({
	name,
	reviews,
}: {
	name: string;
	reviews: string[];
}) => {
	const { t } = useTranslation();
	const isIncomplete = reviews.includes(ReviewAction.REJECT);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;

	return (
		<div className="flex flex-row items-center">
			<Icon src={isIncomplete ? RedTick : GreenTick} width={18} height={18} />
			<Text
				className={`font-sans font-bold text-gray-650 text-sm ml-8 mr-2 ${
					isMobile ? 'break-words max-w-[200px] leading-tight' : ''
				}`}
			>{`${t(name)}`}</Text>
		</div>
	);
};

export const CategoryScore = ({
	uiConfig,
	self,
	selfAnswers,
	applicantInterview,
	applicantInterviewAnswers,
	preceptorInterview,
	preceptorInterviewAnswers,
	familyMemberInterview,
	familyMemberInterviewAnswers,
}: {
	uiConfig: CategoryStepUIConfig;
	self?: Feedback;
	selfAnswers?: Answers;
	applicantInterview?: Feedback;
	applicantInterviewAnswers?: Answers;
	preceptorInterview?: Feedback;
	preceptorInterviewAnswers?: Answers;
	familyMemberInterview?: Feedback;
	familyMemberInterviewAnswers?: Answers;
}) => {
	const feedbacks = useMemo(() => {
		return [
			self,
			applicantInterview,
			preceptorInterview,
			familyMemberInterview,
		];
	}, [applicantInterview, familyMemberInterview, preceptorInterview, self]);
	const reviews = feedbacks
		.map((feedback) => {
			if (feedback && feedback?.review) {
				return feedback.review;
			}
			return null as unknown as string;
		})
		.filter(Boolean);

	return (
		<Collapse
			ghost
			expandIconPosition="end"
			expandIcon={({ isActive }) => (
				<span
					className={`transition-transform ${
						isActive ? 'rotate-180' : 'rotate-0'
					}`}
				>
					<Icon src={GrayChevronDown} width={18} height={18} />
				</span>
			)}
			defaultActiveKey={['1']}
		>
			<Panel
				className="mb-4 !border !border-solid !border-gray-200 !rounded"
				header={<Header name={uiConfig.title} reviews={reviews} />}
				key="1"
			>
				<div className="flex flex-row flex-wrap">
					{self && (
						<ReviewerResultTag
							reviewer="application"
							result={self}
							answers={selfAnswers}
							uiConfig={uiConfig}
							name={uiConfig.title}
						/>
					)}
					{applicantInterview && (
						<ReviewerResultTag
							reviewer="candidateInterview"
							result={applicantInterview}
							answers={applicantInterviewAnswers}
							uiConfig={uiConfig}
							name={uiConfig.title}
						/>
					)}
					{preceptorInterview && (
						<ReviewerResultTag
							reviewer="preceptor"
							result={preceptorInterview}
							answers={preceptorInterviewAnswers}
							uiConfig={uiConfig}
							name={uiConfig.title}
						/>
					)}
					{familyMemberInterview && (
						<ReviewerResultTag
							reviewer="family"
							result={familyMemberInterview}
							answers={familyMemberInterviewAnswers}
							uiConfig={uiConfig}
							name={uiConfig.title}
						/>
					)}
				</div>
			</Panel>
		</Collapse>
	);
};

export const FinalRecommendationScore = ({
	name,
	preceptor,
	proposer,
	approver,
	otherReference,
}: {
	name: string;
	preceptor?: Feedback;
	proposer?: Feedback;
	approver?: Feedback;
	otherReference?: Feedback;
}) => {
	const feedbacks = useMemo(() => {
		return [preceptor, proposer, approver];
	}, [approver, preceptor, proposer]);
	const reviews = feedbacks
		.map((feedback) => {
			if (feedback && feedback?.review) {
				return feedback.review;
			}
			return null as unknown as string;
		})
		.filter(Boolean);

	return (
		<Collapse
			ghost
			expandIconPosition="end"
			expandIcon={({ isActive }) => (
				<span
					className={`transition-transform ${
						isActive ? 'rotate-180' : 'rotate-0'
					}`}
				>
					<Icon src={GrayChevronDown} width={18} height={18} />
				</span>
			)}
			defaultActiveKey={['1']}
		>
			<Panel
				className="mb-4 !border !border-solid !border-gray-200 !rounded"
				header={<Header name={name} reviews={reviews} />}
				key="1"
			>
				<div className="flex flex-row">
					{preceptor && (
						<ReviewerResultTag
							name={name}
							reviewer="preceptor"
							result={preceptor}
							hideGoToQuestions
						/>
					)}
					{proposer && (
						<ReviewerResultTag
							name={name}
							reviewer="proposer"
							result={proposer}
							hideGoToQuestions
						/>
					)}
					{approver && (
						<ReviewerResultTag
							name={name}
							reviewer="approver"
							result={approver}
							hideGoToQuestions
						/>
					)}
					{otherReference && (
						<ReviewerResultTag
							name={name}
							reviewer="otherReference"
							result={otherReference}
							hideGoToQuestions
						/>
					)}
				</div>
			</Panel>
		</Collapse>
	);
};
